/**
 * Reusable helpers for rendering "code state" (removed / connect-added / claim financial
 * outcome) on a billed procedure code. Used by the billing views and patient-file coding
 * list so all three surfaces agree on labels and precedence.
 */

export interface CodeStateChip {
  label: string;
  cls: string;
}

export interface CodeStateView {
  removed: boolean;
  connectAdded: boolean;
  chips: CodeStateChip[];
}

/**
 * Derive the display state for a procedure code from its raw (Connect-enriched) fields.
 * Pure function — call once when building the row, not from the template.
 *
 * ── Two rules worth stating, because getting either wrong is silent ──
 *
 * 1. SETTLEMENT IS MEASURED AGAINST WHAT IS COLLECTABLE, i.e. approved + patient-liable —
 *    never against `approved` alone. Two failures came from the latter:
 *      · a claim the scheme REJECTED and the patient then paid in full showed no chip at
 *        all, because both the paid and part-paid branches required `approved > 0` and a
 *        rejected claim approves nothing (it routes the whole amount to patient-liable);
 *      · an ordinary co-payment claim flipped to "Paid" the moment the scheme settled its
 *        share, while the patient still owed theirs.
 *
 * 2. A LINE WITH NO BILLED AMOUNT CARRIES NO RECEIVABLE, so it can never be paid and must
 *    never be branded from the CLAIM's status. These are the structural modifier markers
 *    (0005 multiple-procedure, 0011 time, 0008/0009 assistant): MediKredit prices them at
 *    0.00 on the wire and returns an empty <FIN/>, so no amount will ever describe them.
 *    Falling back to the claim status stamped every such line on a rejected claim as
 *    "Rejected" forever — a state no payment could clear. They read "No charge" instead,
 *    and only MediKredit's verdict for THAT line (`connectLineStatus`) overrides it,
 *    because a disallowed modifier is a real coding signal worth chasing.
 */
export function getCodeStateView(code: any): CodeStateView {
  const removed = code?.status === 'removed';
  const connectAdded = code?.source === 'connect_added';

  const chips: CodeStateChip[] = [];

  const billed = toCentsOrNull(code?.connectBilledCents);
  const approved = toCentsOrNull(code?.connectApprovedCents);
  const rejected = toCentsOrNull(code?.connectRejectedCents);
  const liable = toCentsOrNull(code?.connectPatientLiableCents);
  const paid = toCentsOrNull(code?.connectPaidCents);
  const clawback = toCentsOrNull(code?.connectClawbackCents);

  // MediKredit's verdict for THIS line, where it gave one.
  const lineStatus = String(code?.connectLineStatus ?? '').trim().toLowerCase();
  const lineRejected = lineStatus === 'rejected';

  // The whole CLAIM's outcome, copied onto every line — a last resort, used only when the
  // line carries no financial data of its own to reason from.
  const connectStatus = String(code?.connectClaimStatus ?? code?.connectSubmissionStatus ?? '')
    .trim().toLowerCase();
  const statusRejected = connectStatus === 'rejected' || connectStatus === 'denied';

  const hasFinancialData =
    billed !== null || approved !== null || rejected !== null || paid !== null || clawback !== null;

  if (hasFinancialData || statusRejected || lineStatus) {
    // Rule 2 — a structural marker: financials have landed and say it was billed at zero.
    const noCharge = billed !== null && billed === 0;

    if (noCharge) {
      chips.push(
        lineRejected
          ? { label: 'Rejected', cls: 'code-chip--rejected' }
          : { label: 'No charge', cls: 'code-chip--no-charge' },
      );
    } else {
      // Rule 1 — what anyone (scheme or patient) still owes on this line.
      const collectable = (approved ?? 0) + (liable ?? 0);
      const paidCents = paid ?? 0;

      if (lineRejected || (collectable === 0 && (rejected ?? 0) > 0)) {
        chips.push({ label: 'Rejected', cls: 'code-chip--rejected' });
      } else if (collectable > 0 && paidCents >= collectable) {
        chips.push({ label: 'Paid', cls: 'code-chip--paid' });
      } else if (paidCents > 0) {
        chips.push({ label: 'Part-paid', cls: 'code-chip--partial' });
      } else if ((rejected ?? 0) > 0) {
        // Partly written down, the remainder still collectable, nothing paid yet.
        chips.push({ label: 'Adjusted', cls: 'code-chip--partial' });
      } else if (!hasFinancialData && statusRejected) {
        // No per-line financials at all — a wholesale rejection is all we know.
        chips.push({ label: 'Rejected', cls: 'code-chip--rejected' });
      }
    }

    // Clawback can co-exist alongside a primary financial chip above.
    if (clawback !== null && clawback > 0) {
      chips.push({ label: 'Clawback', cls: 'code-chip--clawback' });
    }
  }

  return { removed, connectAdded, chips };
}

function toCentsOrNull(value: any): number | null {
  return value === null || value === undefined ? null : Number(value);
}
